import React, { useState } from 'react';
import {
  Rocket,
  Cloud,
  Code2,
  Database,
  Link2,
  BarChart3,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Layers,
  Cpu,
} from 'lucide-react';
import InforCSIImage from '../../../../../../Assets/infor-csi.jpg';

const csiServices = [
  {
    id: 'implementations',
    title: 'Implementations',
    heading: 'Turnkey Infor CloudSuite Industrial Implementation',
    icon: Rocket,
    tagline: 'From Strategy & Process Blueprinting to Go-Live Mastery',
    description:
      'We orchestrate end-to-end Infor CloudSuite Industrial implementations tailored around your operating model. Combining certified SyteLine architecture with agile delivery methodologies, our specialists harmonize finance, procurement, complex multi-level BOMs, shop-floor dispatch, and logistics into a single unified cloud digital core.',
    points: [
      'Comprehensive Fit-Gap Analysis & Industry Blueprinting',
      'Advanced Multi-Site Configuration & Work Order Orchestration',
      'User Acceptance Testing (UAT) & Simulated Production Runs',
      'Cutover Readiness Audits & Dedicated Hypercare Support',
    ],
    metric: '40% Faster Deployment with PT KCS Accelerators',
  },
  {
    id: 'upgrades',
    title: 'Cloud Migrations',
    heading: 'Zero-Disruption Migration to Infor Cloud on AWS',
    icon: Cloud,
    tagline: 'Modernize from Legacy SyteLine 7/8/9 or On-Premise ERPs',
    description:
      'Transition smoothly from legacy on-premise infrastructure to Infor multi-tenant cloud on AWS. Our structured modernization framework audits legacy schema modifications, sanitizes historical databases, re-architects custom logic into Infor Mongoose extensions, and guarantees continuous production uptime during cutover.',
    points: [
      'Legacy Customization & Database Dependency Audit',
      'Automated Schema Conversion to Cloud Architecture',
      'Rigorous Parallel Testing & Risk Mitigation Protocols',
      'Seamless Multi-Tenant AWS Deployment with 99.9% SLA',
    ],
    metric: 'Zero Operational Downtime During Production Cutover',
  },
  {
    id: 'customizations',
    title: 'Extensibility',
    heading: 'Upgrade-Safe Infor Mongoose Application Development',
    icon: Code2,
    tagline: 'Tailored Business Logic Without Modifying Core System Code',
    description:
      'Address bespoke manufacturing rules without creating technical debt. We leverage the Infor Mongoose metadata architecture to build custom forms, automated event handlers, role-based workflows, and specialized industrial screens that remain 100% upgrade-compatible through all future Infor cloud releases.',
    points: [
      'Infor Mongoose Framework Application Development',
      'Automated Event Triggers & Complex Validation Rules',
      'Role-Specific Dashboard & User Interface Personalization',
      'Future-Proof Code Isolation for Automated Updates',
    ],
    metric: '100% Compatibility with Infor Cloud Monthly Updates',
  },
  {
    id: 'data-migrations',
    title: 'Data Engineering',
    heading: 'High-Integrity ERP Data Migration & Reconciliation',
    icon: Database,
    tagline: 'Clean, Validated Master and Transactional Data from Day One',
    description:
      'ERP success hinges on clean master and transactional data. Our specialized data engineering team manages entity mapping, duplicate deduplication, schema normalization, and automated validation algorithms across Item Masters, multi-level Bills of Materials (BOM), Vendor Catalogs, Open AR/AP, and historical transactions.',
    points: [
      'Automated Item Master & Multi-Level BOM Normalization',
      'Data Scrubbing, Deduplication & Quality Verification',
      'Iterative Mock Cutover Cycles & Discrepancy Reconciliation',
      'Regulatory Compliance & Historical Audit Retention',
    ],
    metric: '99.98% Data Extraction & Mapping Reconciliation Accuracy',
  },
  {
    id: 'integrations',
    title: 'Integrations & API',
    heading: 'Enterprise Infor ION & Hybrid API Integration Architecture',
    icon: Link2,
    tagline: 'Real-Time Bi-Directional Synchronization Across the Tech Stack',
    description:
      'Eliminate operational silos by connecting Infor CSI to your broader enterprise ecosystem. Using Infor ION, Infor OS, and enterprise REST APIs, we build resilient real-time connections to CRM (Salesforce, Dynamics), PLM (Siemens, PTC), Warehouse Systems (WMS), CAD tools, and industrial IoT machine sensors.',
    points: [
      'Infor ION Enterprise Service Bus (ESB) Workflows',
      'Bi-Directional CAD/PLM to Infor CSI BOM Synchronization',
      'IoT Machine Telemetry & Shop Floor Data Capture (SFDC)',
      'Secure Third-Party Logistics (3PL) & EDI Supplier Gateways',
    ],
    metric: 'Sub-Second Event Dispatching Across Connected Apps',
  },
  {
    id: 'optimization',
    title: 'System Optimization',
    heading: 'Continuous Performance Tuning & Operational Diagnostics',
    icon: BarChart3,
    tagline: 'Maximizing Transaction Speed, User Adoption & ERP ROI',
    description:
      'Eliminate performance bottlenecks and streamline complex workflows. Our certified solution architects perform deep database indexing, APS scheduling optimization, bottleneck discovery, and user journey audits to accelerate processing times and unlock hidden operational throughput.',
    points: [
      'APS (Advanced Planning & Scheduling) Parameter Calibration',
      'Database Indexing, Query Optimization & Latency Reduction',
      'Workflow Simplification & Approval Bottleneck Removal',
      'Executive KPI Cockpit & Real-Time Reporting Tuning',
    ],
    metric: 'Up to 45% Boost in Transaction & MRP Run Speeds',
  },
];

const CSIOverview = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeService = csiServices[activeIndex];
  const ActiveIcon = activeService.icon;

  return (
    <section
      id="csi-overview"
      className="px-4 py-16 sm:px-6 lg:px-8 lg:py-24"
      style={{ backgroundColor: '#F8FAFC' }}
    >
      <div className="mx-auto max-w-7xl">
        {/* Section Header */}
        <div className="text-center max-w-6xl mx-auto mb-14">

          <h2
            className="font-satoshi text-2xl font-bold leading-tight sm:text-3xl md:text-4xl"
            style={{ color: '#0F172A', fontFamily: "'Satoshi', sans-serif" }}
          >
            Modernize Core Operations with Infor CloudSuite Industrial
          </h2>

          <p className="mt-4 text-base sm:text-lg leading-relaxed" style={{ color: '#475569' }}>
            Tailored advisory, implementation, and cloud engineering services designed to turn complex 
            discrete manufacturing challenges into sustainable competitive advantages.
          </p>
        </div>

        {/* Tab Navigation Buttons */}
        <div className="mb-10 flex flex-wrap justify-center gap-2.5">
          {csiServices.map((service, index) => {
            const isActive = index === activeIndex;
            const Icon = service.icon;
            return (
              <button
                key={service.id}
                type="button"
                onClick={() => setActiveIndex(index)}
                className="inline-flex items-center gap-2.5 rounded-xl px-5 py-3 text-sm font-semibold transition-all duration-300"
                style={{
                  backgroundColor: isActive ? '#1D4ED8' : '#FFFFFF',
                  color: isActive ? '#FFFFFF' : '#334155',
                  borderColor: isActive ? '#1D4ED8' : '#E2E8F0',
                  borderWidth: '1px',
                  boxShadow: isActive
                    ? '0 10px 25px rgba(29, 78, 216, 0.25)'
                    : '0 2px 6px rgba(15, 23, 42, 0.04)',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = '#F1F5F9';
                    e.currentTarget.style.borderColor = '#CBD5E1';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = '#FFFFFF';
                    e.currentTarget.style.borderColor = '#E2E8F0';
                  }
                }}
              >
                <Icon
                  className="h-4 w-4"
                  style={{ color: isActive ? '#FFFFFF' : '#2563EB' }}
                />
                <span>{service.title}</span>
              </button>
            );
          })}
        </div>

        {/* Detail Display: Split Card Layout */}
        <div
          className="relative overflow-hidden rounded-3xl border transition-all duration-500"
          style={{
            backgroundColor: '#FFFFFF',
            borderColor: '#E2E8F0',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.06)',
          }}
        >
          {/* Subtle Ambient Radial Backing */}
          <div
            className="pointer-events-none absolute -right-40 -top-40 h-96 w-96 rounded-full blur-3xl"
            style={{ background: 'radial-gradient(circle, rgba(37, 99, 235, 0.12) 0%, transparent 70%)' }}
          />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 p-6 sm:p-10 lg:p-12 items-center">
            {/* Left Content Column (7 cols) */}
            <div className="lg:col-span-7 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  {/* <div
                    className="flex h-12 w-12 items-center justify-center rounded-2xl shrink-0"
                    style={{
                      backgroundColor: '#EFF6FF',
                      color: '#1D4ED8',
                      border: '1px solid #BFDBFE',
                    }}
                  >
                    <ActiveIcon className="h-6 w-6" style={{ color: '#1D4ED8' }} />
                  </div> */}
                  <div>
                    <h3
                      className="text-xl sm:text-2xl md:text-3xl font-semibold tracking-tight"
                      style={{ color: '#0F172A' }}
                    >
                      {activeService.heading}
                    </h3>
                  </div>
                </div>

                <p
                  className="mt-4 text-base leading-relaxed"
                  style={{ color: '#334155',wordBreak: 'normal',overflowWrap: 'normal',hyphens: 'none',whieSpace: 'normal' }}
                >
                  {activeService.description}
                </p>

                {/* Scope Checklist Grid */}
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  {activeService.points.map((point, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-3 rounded-xl p-3 border"
                      style={{
                        backgroundColor: '#F8FAFC',
                        borderColor: '#E2E8F0',
                      }}
                    >
                      <CheckCircle2
                        className="h-5 w-5 shrink-0 mt-0.5"
                        style={{ color: '#1D4ED8' }}
                      />
                      <span className="text-xs sm:text-sm font-semibold" style={{ color: '#1E293B' }}>
                        {point}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column: MNC Enterprise System Visual (5 cols) */}
            <div className="lg:col-span-5 relative">
              <div
                className="relative overflow-hidden rounded-2xl border shadow-xl group"
                style={{
                  borderColor: '#CBD5E1',
                  backgroundColor: '#0F172A',
                }}
              >
                <img
                  src={InforCSIImage}
                  alt="Infor CloudSuite Industrial UI and Architecture"
                  className="w-full h-80 sm:h-96 object-cover object-center transition-transform duration-500 group-hover:scale-105"
                />

                {/* Overlay gradient */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      'linear-gradient(180deg, rgba(15, 23, 42, 0.08) 0%, rgba(15, 23, 42, 0.62) 100%)',
                  }}
                />


                {/* Bottom Floating Card */}
                <div
                  className="absolute bottom-4 left-4 right-4 rounded-xl p-3.5 backdrop-blur-md border text-xs"
                  style={{
                    backgroundColor: 'rgba(15, 23, 42, 0.85)',
                    borderColor: 'rgba(56, 189, 248, 0.3)',
                    color: '#FFFFFF',
                  }}
                >
                  <div className="flex items-center gap-2 font-bold mb-1" style={{ color: '#38BDF8' }}>
                    <Cpu className="h-4 w-4" style={{ color: '#38BDF8' }} />
                    <span>Real-Time Advanced Planning & Scheduling</span>
                  </div>
                  <p className="text-[11px] leading-relaxed" style={{ color: '#CBD5E1' }}>
                    Synchronized finite capacity planning, material constraint resolution, and multi-site production schedules.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CSIOverview;
